import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useBulkUpsertMappings, useMappingsByProvider } from '@/hooks/use-mappings';
import { fieldMetadata, type ActiveProvider, type InternalTargetField, type MappingFormData } from '@fame/shared';
import { Plus, Save, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

interface FieldMappingEditorProps {
  provider: ActiveProvider;
}

interface MappingRow {
  id?: string;
  sourceFieldId: string;
  internalTarget: InternalTargetField | '';
}

const emptyMapping: MappingRow = { sourceFieldId: '', internalTarget: '' };

const LEGACY_INTERNAL_TARGET_MAP: Record<string, InternalTargetField> = {
  invoice_email: 'email',
};

const validInternalTargets = new Set(fieldMetadata.map(field => field.key));

function normalizeInternalTarget(rawTarget: string): InternalTargetField | '' {
  if (rawTarget in LEGACY_INTERNAL_TARGET_MAP) {
    return LEGACY_INTERNAL_TARGET_MAP[rawTarget];
  }

  if (validInternalTargets.has(rawTarget as InternalTargetField)) {
    return rawTarget as InternalTargetField;
  }

  return '';
}

export function FieldMappingEditor({ provider }: FieldMappingEditorProps) {
  const { data: existingMappings, isLoading } = useMappingsByProvider(provider);
  const bulkUpsert = useBulkUpsertMappings();
  const [editedMappings, setEditedMappings] = useState<MappingRow[] | null>(null);
  const mappings = useMemo(() => {
    if (editedMappings) {
      return editedMappings;
    }

    if (isLoading) {
      return [];
    }

    if (existingMappings && existingMappings.length > 0) {
      const fieldOrder = new Map(fieldMetadata.map((field, index) => [field.key, index]));
      return [...existingMappings]
        .sort((a, b) => (fieldOrder.get(a.internalTarget) ?? 999) - (fieldOrder.get(b.internalTarget) ?? 999))
        .map(mapping => ({
          id: mapping.id,
          sourceFieldId: mapping.sourceFieldId,
          internalTarget: normalizeInternalTarget(mapping.internalTarget),
        }));
    }

    return [emptyMapping];
  }, [editedMappings, existingMappings, isLoading]);

  const addMapping = () => {
    setEditedMappings([...mappings, emptyMapping]);
  };

  const removeMapping = (index: number) => {
    setEditedMappings(mappings.filter((_, itemIndex) => itemIndex !== index));
  };

  const updateMapping = (index: number, field: keyof MappingRow, value: string) => {
    setEditedMappings(
      mappings.map((mapping, itemIndex) => (itemIndex === index ? { ...mapping, [field]: value } : mapping))
    );
  };

  const handleSave = async () => {
    // Filter out empty rows
    const validMappings = mappings.filter(
      m =>
        m.sourceFieldId.trim().length > 0 &&
        m.internalTarget &&
        validInternalTargets.has(m.internalTarget as InternalTargetField)
    );

    const formData: MappingFormData[] = validMappings.map(m => ({
      provider,
      sourceFieldId: m.sourceFieldId.trim(),
      internalTarget: m.internalTarget as InternalTargetField,
    }));

    await bulkUpsert.mutateAsync({ provider, mappings: formData });
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'required':
        return 'Verplicht';
      case 'participant':
        return 'Deelnemer';
      case 'primary':
        return 'Factuur P1';
      case 'split':
        return 'Factuur P2';
      case 'calculation':
        return 'Prijsberekening';
      case 'logic':
        return 'Logica';
      default:
        return category;
    }
  };

  const getCategoryColor = (category: string): 'default' | 'secondary' | 'outline' | 'destructive' => {
    switch (category) {
      case 'required':
        return 'destructive';
      case 'participant':
      case 'primary':
        return 'default';
      case 'split':
        return 'secondary';
      case 'calculation':
        return 'outline';
      case 'logic':
        return 'outline';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'string':
        return 'text';
      case 'email':
        return 'email';
      case 'number':
        return 'nummer';
      case 'boolean':
        return 'ja/nee';
      case 'array':
        return 'multi';
      default:
        return type;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-full" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Veld Mapping</CardTitle>
        <CardDescription>
          Map externe formuliervelden naar het interne schema. Bijvoorbeeld: Jotform veld "q4_name" →
          "invoice_last_name"
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {mappings.map((mapping, index) => (
            <div key={index} className="grid grid-cols-12 gap-2 items-start">
              <div className="col-span-5">
                <Input
                  placeholder="bijv. q4_name"
                  value={mapping.sourceFieldId}
                  onChange={e => updateMapping(index, 'sourceFieldId', e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">Extern veld ID</p>
              </div>

              <div className="col-span-6">
                <Select
                  value={mapping.internalTarget}
                  onValueChange={value => updateMapping(index, 'internalTarget', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer intern veld" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Group by category - show 'required' first */}
                    {['required', 'participant', 'primary', 'split', 'calculation', 'logic'].map(category => (
                      <div key={category}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                          {getCategoryLabel(category)}
                        </div>
                        {fieldMetadata
                          .filter(f => f.category === category)
                          .map(field => (
                            <SelectItem key={field.key} value={field.key}>
                              <div className="flex items-center justify-between gap-2 w-full">
                                <div className="flex items-center gap-2">
                                  <span>{field.label}</span>
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                    {getTypeLabel(field.type)}
                                  </span>
                                </div>
                                {field.description && (
                                  <span className="text-xs text-muted-foreground">{field.description}</span>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
                {mapping.internalTarget && (
                  <div className="mt-1">
                    <Badge
                      variant={getCategoryColor(
                        fieldMetadata.find(f => f.key === mapping.internalTarget)?.category || 'default'
                      )}
                      className="text-xs"
                    >
                      {fieldMetadata.find(f => f.key === mapping.internalTarget)?.category}
                    </Badge>
                  </div>
                )}
              </div>

              <div className="col-span-1 flex justify-end">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeMapping(index)}
                  disabled={mappings.length === 1}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <Button variant="outline" size="sm" onClick={addMapping}>
            <Plus className="mr-2 h-4 w-4" />
            Veld Toevoegen
          </Button>

          <Button onClick={handleSave} disabled={bulkUpsert.isPending}>
            <Save className="mr-2 h-4 w-4" />
            Opslaan
          </Button>
        </div>

        {/* Warning if group_id is not mapped */}
        {!mappings.some(m => m.internalTarget === 'group_id') && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm">
            <p className="font-semibold text-red-900 mb-2">Let op: Verplicht veld ontbreekt</p>
            <p className="text-red-800">
              Het veld <strong>"Groep ID"</strong> moet gemapped worden om webhooks correct te laten werken. Zonder dit
              veld kan het systeem niet bepalen bij welke groep een inzending hoort.
            </p>
          </div>
        )}

        <div className="mt-4 p-4 bg-muted rounded-lg text-sm">
          <p className="font-semibold mb-2">Tip:</p>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>
              <strong>Groep ID</strong> is verplicht en moet gemapped worden aan een veld in je formulier (bijvoorbeeld
              een verborgen veld met de groep UUID)
            </li>
            <li>Het externe veld ID vind je in je formulier provider (bijv. Jotform: "q4_name")</li>
            <li>P2 velden worden automatisch gebruikt bij split facturatie</li>
            <li>Calculation velden beïnvloeden de prijs via de calculation rules</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
